import bigInt from 'big-integer';

export const baseConversions = {
  'Decimal': 10,
  'Binary': 2,
  'Octal': 8,
  'Hexadecimal': 16,
  'Base-36': 36
};

export const specialConversions = {
  'Roman Numeral': {
    toDecimal: (roman) => {
      const romanValues = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
      let result = 0;
      for (let i = 0; i < roman.length; i++) {
        const current = romanValues[roman[i]];
        const next = romanValues[roman[i + 1]];
        if (next && next > current) {
          result += next - current;
          i++;
        } else {
          result += current;
        }
      }
      return result;
    },
    fromDecimal: (num) => {
      const romanSymbols = [
        { value: 1000, symbol: 'M' },
        { value: 900, symbol: 'CM' },
        { value: 500, symbol: 'D' },
        { value: 400, symbol: 'CD' },
        { value: 100, symbol: 'C' },
        { value: 90, symbol: 'XC' },
        { value: 50, symbol: 'L' },
        { value: 40, symbol: 'XL' },
        { value: 10, symbol: 'X' },
        { value: 9, symbol: 'IX' },
        { value: 5, symbol: 'V' },
        { value: 4, symbol: 'IV' },
        { value: 1, symbol: 'I' }
      ];
      let result = '';
      for (let { value, symbol } of romanSymbols) {
        while (num >= value) {
          result += symbol;
          num -= value;
        }
      }
      return result;
    }
  },
  'Scientific Notation': {
    toDecimal: (sci) => {
      const [base, exponent] = sci.split('e');
      return parseFloat(base) * Math.pow(10, parseInt(exponent));
    },
    fromDecimal: (num) => {
      const exp = Math.floor(Math.log10(Math.abs(num)));
      const base = num / Math.pow(10, exp);
      return `${base.toFixed(4)}e${exp}`;
    }
  },
  'Base-64': {
    toDecimal: (base64) => {
      const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
      let result = bigInt(0);
      for (let i = 0; i < base64.length; i++) {
        const charIndex = base64Chars.indexOf(base64[i]);
        if (charIndex === -1) {
          throw new Error(`Invalid Base-64 character: ${base64[i]}`);
        }
        result = result.multiply(64).add(charIndex);
      }
      return result.toString(); // Returning as string to handle large numbers
    },
    fromDecimal: (num) => {
      const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
      let bigNum = bigInt(num);
      if (bigNum.isNegative()) {
        throw new Error('Negative numbers cannot be converted to Base-64.');
      }
      let result = '';
      if (bigNum.equals(0)) {
        return 'A'; // Assuming 'A' represents 0 in Base-64
      }
      while (bigNum.greater(0)) {
        const remainder = bigNum.mod(64).toJSNumber(); // Convert to JavaScript number
        if (remainder < 0 || remainder >= 64) {
          throw new Error(`Invalid remainder during Base-64 conversion: ${remainder}`);
        }
        result = base64Chars[remainder] + result;
        bigNum = bigNum.divide(64);
      }
      return result.toString(); // Ensuring it's a string
    },
    charToValue: (char) => {
      const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
      return base64Chars.indexOf(char);
    },
    encode: (text) => {
      return btoa(text);
    },
    decode: (encoded) => {
      return atob(encoded);
    }
  }
};

export const convertNumber = (value, fromUnit, toUnit, isEncoding = false) => {
  if (isEncoding || (fromUnit === 'Base-64' || toUnit === 'Base-64')) {
    if (fromUnit === 'Decimal' && toUnit === 'Base-64') {
      const decimalValue = parseInt(value, 10);
      const byteArray = new Uint8Array([
        (decimalValue >> 24) & 0xFF,
        (decimalValue >> 16) & 0xFF,
        (decimalValue >> 8) & 0xFF,
        decimalValue & 0xFF
      ]);
      return btoa(String.fromCharCode.apply(null, byteArray));
    } else if (fromUnit === 'Base-64' && toUnit === 'Decimal') {
      const decoded = atob(value);
      const bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i);
      }
      return new DataView(bytes.buffer).getUint32(0).toString();
    } else if (fromUnit === 'Base-64') {
      return atob(value);
    } else if (toUnit === 'Base-64') {
      return btoa(value);
    }
  }

  // Existing conversion logic
  let decimalValue;

  // Convert from source to decimal
  if (fromUnit === 'Base-64') {
    decimalValue = specialConversions['Base-64'].toDecimal(value);
  } else if (fromUnit in baseConversions) {
    decimalValue = parseInt(value, baseConversions[fromUnit]);
    if (isNaN(decimalValue)) {
      throw new Error(`Unable to convert ${value} from ${fromUnit}.`);
    }
  } else if (fromUnit in specialConversions) {
    decimalValue = specialConversions[fromUnit].toDecimal(value);
  } else {
    throw new Error('Unsupported source number system');
  }

  // Convert from decimal to target
  if (toUnit === 'Base-64') {
    return specialConversions['Base-64'].fromDecimal(decimalValue);
  } else if (toUnit in baseConversions) {
    return decimalValue.toString(baseConversions[toUnit]).toUpperCase(); // Ensure uppercase
  } else if (toUnit in specialConversions) {
    return specialConversions[toUnit].fromDecimal(decimalValue);
  } else {
    throw new Error('Unsupported target number system');
  }
};